// Account Comparison · interactive side-by-side specs for all 4 tiers.
// Filter chips toggle which tiers are visible, "Compare by" tabs highlight
// the rows relevant to the chosen lens (cost · execution · platform · limits).
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Check, X, Filter } from "lucide-react";
import { motion } from "framer-motion";
import MarketingShell from "@/components/MarketingShell";
import { TIER_META } from "@/components/AccountTypesSection";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";
import { useCtaTarget } from "@/lib/useCtaTarget";
import { useTheme } from "@/contexts/ThemeContext";

const ALL_TIERS = ["classic", "premier", "elite", "social", "cent"];

/* Row definitions · `key` maps to compare.rows.<key> translation key.
   `lens` controls dimming when a specific lens is selected. */
const ROW_DEFS = [
  { key: "minDeposit",       lens: "cost",      get: (a) => a.minDeposit },
  { key: "spread",           lens: "cost",      getRaw: (a, t) => `${a.spreadFrom} ${t("compare.cell.pips", "pips")}` },
  { key: "commission",       lens: "cost",      get: (a) => a.commission },
  { key: "leverage",         lens: "limits",    get: (a) => a.leverage },
  { key: "execution",        lens: "execution", get: (a) => a.execution },
  { key: "baseCurrencies",   lens: "limits",    get: (a) => a.baseCurrencies.join(" · ") },
  { key: "lotRange",         lens: "limits",    get: (a) => `${a.minLot} / ${a.maxLot}` },
  { key: "eas",              lens: "platform",  get: () => true },
  { key: "hedging",          lens: "execution", get: () => true },
  { key: "islamic",          lens: "platform",  get: (a) => a.label === "Classic" || a.label === "Cent" },
  { key: "vps",              lens: "platform",  get: (a) => a.label === "Premier" || a.label === "Elite" },
  { key: "manager",          lens: "platform",  get: () => true },
  { key: "copyTrading",      lens: "platform",  get: (a) => a.label === "Social" },
];

const LENS_KEYS = ["all", "cost", "execution", "platform", "limits"];

function Cell({ value, dim }) {
  const cls = dim ? "opacity-35" : "";
  if (value === true)  return <Check size={16} className={`mx-auto text-[color:var(--brand)] ${cls}`} strokeWidth={2.3} />;
  if (value === false) return <X size={16} className={`mx-auto text-text-3 ${cls}`} strokeWidth={2} />;
  return <span className={`font-mono text-[13px] tabular-nums text-text-1 ${cls}`}>{value}</span>;
}

/* Compact variant for the per-row 4-up mobile matrix · smaller text,
 * tighter tracking, allows wrapping so longer values like
 * "EUR · USD · GBP · ZAR" still fit inside an ~80 px cell. */
function CompactCell({ value, accent }) {
  if (value === true)  return <Check size={14} className="mx-auto" strokeWidth={2.4} style={{ color: accent }} />;
  if (value === false) return <X size={12} className="mx-auto text-text-3" strokeWidth={2} />;
  return (
    <span
      className="block font-mono text-[10.5px] tabular-nums text-text-1 leading-tight break-words"
      style={{ wordBreak: "break-word" }}
    >
      {value}
    </span>
  );
}

export default function AccountComparisonPage() {
  // Empty selection = neutral state · ALL tiers shown, chips render as
  // gradient-outline ghosts. Clicking fills the chip + filters columns.
  const [visibleTiers, setVisibleTiers] = useState(new Set());
  const [lens, setLens] = useState("all");
  const { t } = useTranslation();
  const { featuredTier, featuredLabel, getBadgeFor, accounts: ACCOUNTS } = useSiteSettings();
  // Theme-aware tier CTA palette · identical to the tier cards on the
  // homepage so "Open"/"View details" buttons match everywhere.
  const { theme } = useTheme();
  const isLight = theme === "light";
  const tierCtaStyle = (tm) => ({
    background: isLight ? tm.gradientLight : tm.gradient,
    color: isLight ? tm.ctaTextLight : tm.ctaText,
    "--gloss-accent": tm.accentDeep,
  });
  const compareCta = useCtaTarget("account_comparison");

  // Localised row + lens metadata · rebuild on each render so the
  // translation hook returns the right locale.
  const rows = useMemo(
    () =>
      ROW_DEFS.map((r) => ({
        ...r,
        label: t(`compare.rows.${r.key}`, r.key),
        slug: r.key,
        get: r.getRaw ? (a) => r.getRaw(a, t) : r.get,
      })),
    [t]
  );
  const lenses = useMemo(
    () => LENS_KEYS.map((k) => ({ key: k, label: t(`compare.lenses.${k}`, k) })),
    [t]
  );

  const tiers = useMemo(
    () => (visibleTiers.size === 0 ? ALL_TIERS : ALL_TIERS.filter((tk) => visibleTiers.has(tk))),
    [visibleTiers]
  );

  const toggleTier = (k) => {
    const next = new Set(visibleTiers);
    if (next.has(k)) next.delete(k); else next.add(k);
    setVisibleTiers(next);
  };

  const tierCol = tiers.length > 0 ? `repeat(${tiers.length}, minmax(0, 1fr))` : "1fr";
  const gridTemplate = { gridTemplateColumns: `minmax(140px, 1.4fr) ${tierCol}` };
  // Ensure each tier column gets a usable width even with all 4 selected on mobile.
  // 140 (label) + 4 * 110 (tier) + gaps → scrolls horizontally inside the wrapper
  // when the viewport is too narrow. On lg+ everything fits naturally.
  const compareMinWidth = 140 + tiers.length * 110 + (tiers.length + 1) * 12;

  return (
    <MarketingShell
      eyebrow={t("compare.eyebrow", "Side-by-side comparison")}
      title={t("compare.title", "Pick the right")}
      titleAccent={t("compare.accent", "OFinancial account")}
      lead={t("compare.lead", "Compare every feature across our five accounts. Filter the accounts you care about, then switch the comparison lens · cost, execution, platform or limits.")}
      primary={{ label: t("compare.cta.openAccount", "Open Account"), href: compareCta.href, external: compareCta.isExternal }}
      secondary={{ label: t("compare.cta.backHome", "Back to home"), to: "/" }}
      testid="account-comparison-page"
    >
      {/* ===== Filter toolbar · centered on mobile, justified on desktop ===== */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-center lg:justify-between gap-3 mb-6">
        {/* Tier filter chips · gloss-styled to match the rest of the site */}
        <div className="flex items-center gap-2 flex-wrap justify-center lg:justify-start" data-testid="compare-tier-filters">
          <div className="inline-flex items-center gap-1.5 h-9 px-3 rounded-[10px] font-mono text-[11px] uppercase tracking-[0.16em] text-text-3" style={{ background: "color-mix(in srgb, var(--text-1) 5%, transparent)" }}>
            <Filter size={11} strokeWidth={2} /> {t("compare.toolbar.accounts", "Accounts")}
          </div>
          {ALL_TIERS.map((k) => {
            const a = ACCOUNTS[k];
            const tm = TIER_META[k];
            const on = visibleTiers.has(k);
            return (
              <button
                key={k}
                type="button"
                onClick={() => toggleTier(k)}
                aria-pressed={on}
                data-testid={`compare-tier-${k}`}
                className={`${on ? "btn-gloss" : "btn-tier-outline"} inline-flex items-center justify-center gap-1.5 h-9 px-3.5 rounded-[10px] text-[12.5px] font-semibold`}
                style={on
                  ? tierCtaStyle(tm)
                  : {
                      "--tier-grad": isLight ? tm.gradientLight : tm.gradient,
                      "--chip-fill": "var(--surface)",
                      "--gloss-accent": tm.accentDeep,
                      color: isLight ? tm.accentDeep : tm.accent,
                    }}
              >
                {a.label}
                {getBadgeFor(k, a.badge) && (
                  <span className="text-[12px] font-mono uppercase tracking-wider opacity-80 hidden sm:inline">
                    · {getBadgeFor(k, a.badge).replace("Most ", "")}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Compare-by lens tabs · always horizontally-scrollable so labels
            like "Limits" never get clipped at the right edge. 'Lens'
            prefix hidden on mobile (it was eating ~50 px and pushing
            chips off the right edge). */}
        <div
          className="-mx-5 sm:mx-0 overflow-x-auto scrollbar-none"
          data-testid="compare-lens-tabs-wrap"
        >
          <div className="inline-flex items-center gap-1 p-1 rounded-[10px] mx-5 sm:mx-0 whitespace-nowrap" style={{ background: "color-mix(in srgb, var(--text-1) 5%, transparent)" }} data-testid="compare-lens-tabs">
            <span className="hidden lg:inline px-2 text-[11px] uppercase tracking-[0.16em] text-text-3 font-mono shrink-0">{t("compare.toolbar.lens", "Lens")}</span>
            {lenses.map((l) => {
              const on = lens === l.key;
              return (
                <button
                  key={l.key}
                  type="button"
                  onClick={() => setLens(l.key)}
                  aria-pressed={on}
                  data-testid={`compare-lens-${l.key}`}
                  className="h-7 px-2.5 rounded-[8px] text-[12px] font-medium transition-all shrink-0"
                  style={on ? { background: "var(--brand)", color: "var(--brand-ink)" } : { color: "var(--text-2)" }}
                >
                  {l.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ---- Mobile/tablet (<xl) · per-row 4-up comparison ·
           ALL 4 tiers always visible on every spec row so users see
           the full picture in one glance, no horizontal scroll. ---- */}
      {tiers.length > 0 && (
        <div className="xl:hidden mb-10" data-testid="compare-stack-mobile">
          {/* Sticky tier header strip · accent stripe + tier name so users
              always know which column belongs to which tier as they scroll. */}
          <div
            className="grid gap-2 mb-3 sticky top-[60px] z-10 py-2.5 px-1 rounded-lg backdrop-blur-xl"
            style={{
              gridTemplateColumns: `repeat(${tiers.length}, minmax(0, 1fr))`,
              background: "color-mix(in srgb, var(--body-bg) 88%, transparent)",
            }}
            data-testid="compare-mobile-header"
          >
            {tiers.map((k) => {
              const at = ACCOUNTS[k];
              const tmk = TIER_META[k];
              const accent = tmk?.accentDeep || "var(--brand)";
              const tierBadge = getBadgeFor(k, at.badge);
              return (
                <div key={k} className="text-center min-w-0" data-testid={`compare-mobile-header-${k}`}>
                  <div className="h-1 w-8 rounded-full mx-auto mb-1.5" style={{ background: accent }} />
                  <div className="font-display text-[13px] font-semibold leading-tight truncate" style={{ color: accent }}>
                    {at.label}
                  </div>
                  <div className="font-mono text-[9.5px] uppercase tracking-wider text-text-3 mt-0.5 tabular-nums truncate">
                    {at.minDeposit}
                  </div>
                  {tierBadge && (
                    <div
                      className="font-mono text-[8.5px] uppercase tracking-wider mt-1 px-1 py-0.5 rounded mx-auto inline-block"
                      style={{ background: `color-mix(in srgb, ${accent} 18%, transparent)`, color: accent }}
                    >
                      {tierBadge.replace("Most ", "")}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Spec rows · spec label on top, N mini-cells below (one per
              visible tier) so users compare ALL accounts at every row. */}
          <div className="rounded-2xl overflow-hidden" style={{ background: "var(--surface-2)" }} data-testid="compare-mobile-rows">
            {rows.map((row, idx) => {
              const dim = lens !== "all" && row.lens !== lens;
              return (
                <div
                  key={row.label}
                  className="px-3 py-3 transition-opacity"
                  style={{
                    borderBottom: idx === rows.length - 1 ? "none" : "1px solid color-mix(in srgb, var(--text-1) 5%, transparent)",
                    opacity: dim ? 0.35 : 1,
                  }}
                  data-testid={`compare-mobile-row-${row.slug}`}
                >
                  <div className="text-text-3 text-[11px] uppercase tracking-[0.14em] font-mono leading-tight mb-2 text-center">
                    {row.label}
                  </div>
                  <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${tiers.length}, minmax(0, 1fr))` }}>
                    {tiers.map((k) => {
                      const a = ACCOUNTS[k];
                      const tmk = TIER_META[k];
                      const accent = tmk?.accentDeep || "var(--brand)";
                      return (
                        <div
                          key={k}
                          className="rounded-md py-1.5 px-1 text-center min-w-0 flex items-center justify-center"
                          style={{
                            background: `color-mix(in srgb, ${accent} 4%, var(--surface))`,
                            minHeight: 30,
                          }}
                          data-testid={`compare-mobile-cell-${k}-${row.slug}`}
                        >
                          <CompactCell value={row.get(a)} accent={accent} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* N Open-account CTAs · one per visible tier, in tier accent
              colour. Grid auto-fits up to 4 tiers in a 2×2 / 1×N layout. */}
          <div
            className="grid gap-2 mt-5"
            style={{ gridTemplateColumns: `repeat(${Math.min(tiers.length, 2)}, minmax(0, 1fr))` }}
            data-testid="compare-mobile-ctas"
          >
            {tiers.map((k) => {
              const at = ACCOUNTS[k];
              const tmk = TIER_META[k];
              return (
                <Link
                  key={k}
                  to={`/account-types/${k}`}
                  className="btn-gloss inline-flex items-center justify-center gap-1.5 h-11 px-3 rounded-[10px] text-[12.5px] font-semibold"
                  style={tierCtaStyle(tmk)}
                  data-testid={`compare-mobile-open-${k}`}
                >
                  <span className="truncate">{t("compare.cta.openTier", { defaultValue: "Open {{tier}}", tier: at.label })}</span> <ArrowRight size={12} className="shrink-0" />
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* ---- Desktop · the original side-by-side matrix (xl+ only) ---- */}
      <div className="hidden xl:block -mx-8 px-8 overflow-x-auto pb-1" data-testid="compare-scroll-wrap">
        <div style={{ minWidth: `${compareMinWidth}px` }}>
          <div
            className="grid gap-3 mb-2 sticky top-[64px] z-20 py-3 backdrop-blur-xl"
            style={{
              background: "color-mix(in srgb, var(--body-bg) 88%, transparent)",
              ...gridTemplate,
            }}
          >
        <div className="font-mono text-[12px] uppercase tracking-[0.22em] text-text-3 pl-3 flex items-center">
          {lens === "all" ? t("compare.heading.specification", "Specification") : `${t("compare.heading.highlighting", "Highlighting")} · ${t(`compare.lenses.${lens}`, lens)}`}
        </div>
        {tiers.map((k) => {
          const a = ACCOUNTS[k];
          const badge = getBadgeFor(k, a.badge);
          const tm = TIER_META[k];
          const accentDeep = tm?.accentDeep || "var(--brand)";
          const isFeatured = k === featuredTier;
          return (
            <motion.div
              key={k}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
              className="rounded-[10px] border p-4 text-center"
              style={{
                borderColor: isFeatured
                  ? accentDeep
                  : `color-mix(in srgb, ${accentDeep} 30%, var(--line-strong))`,
                background: `color-mix(in srgb, ${accentDeep} ${isFeatured ? 9 : 5}%, var(--surface))`,
                borderTopWidth: 3,
                borderTopColor: accentDeep,
              }}
              data-testid={`compare-header-${k}`}
            >
              <div className="font-display text-base sm:text-lg font-semibold leading-tight" style={{ color: accentDeep }}>{a.label}</div>
              <div className="font-mono text-[12px] uppercase tracking-wider text-text-3 mt-1">{a.minDeposit} {t("compare.cell.min", "min")}</div>
              {badge && (
                <div
                  className="font-mono text-[12px] uppercase tracking-widest mt-1.5 inline-block px-2 py-0.5 rounded-[6px]"
                  style={{ background: `color-mix(in srgb, ${accentDeep} 18%, transparent)`, color: accentDeep }}
                >
                  {badge}
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* ===== Specs rows ===== */}
      <div className="rounded-2xl border overflow-hidden mb-10" style={{ borderColor: "var(--line-strong)", background: "var(--surface)" }} data-testid="compare-table">
        {rows.map((row, idx) => {
          const dim = lens !== "all" && row.lens !== lens;
          return (
            <div
              key={row.label}
              className="grid gap-3 items-center px-3 py-3.5 border-b last:border-0 transition-opacity"
              style={{
                ...gridTemplate,
                borderColor: "var(--line)",
                background: idx % 2 === 1 ? "color-mix(in srgb, var(--surface-2) 50%, transparent)" : "transparent",
                opacity: dim ? 0.5 : 1,
              }}
              data-testid={`compare-row-${row.slug}`}
            >
              <div className="text-text-3 text-[12px] sm:text-[12px] uppercase tracking-wider font-mono pl-3 leading-tight">{row.label}</div>
              {tiers.map((k) => (
                <div key={k} className="text-center">
                  <Cell value={row.get(ACCOUNTS[k])} dim={false} />
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* ===== Open-account row · desktop only · tier-coloured CTAs ===== */}
      <div className="grid gap-3 mb-12" style={gridTemplate}>
        <div />
        {tiers.map((k) => {
          const tm = TIER_META[k];
          return (
            <Link
              key={k}
              to={`/account-types/${k}`}
              className="btn-gloss inline-flex items-center justify-center gap-1.5 h-11 px-4 rounded-[10px] text-[12.5px] font-semibold"
              style={tierCtaStyle(tm)}
              data-testid={`compare-open-${k}`}
            >
              <span className="hidden sm:inline">{t("compare.cta.viewDetails", "View details")}</span>
              <span className="sm:hidden">{t("compare.cta.view", "View")}</span>
              <ArrowRight size={13} />
            </Link>
          );
        })}
      </div>
        </div>
      </div>
    </MarketingShell>
  );
}
